import { Component } from '@theme/component';
import { debounce, isClickedOutside, isMobileHeaderPanel, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo, getScrollEventTarget } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.#desktopSearchController?.abort();
    this.#desktopSearchOpen = false;
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  #mobileSearchOpening = false;
  #desktopSearchOpen = false;
  #desktopSearchClosing = false;
  /** @type {AbortController | null} */
  #desktopSearchController = null;
  /** @type {HTMLElement | null} */
  #desktopSearchTrigger = null;

  /**
   * Shows the dialog.
   */
  async showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#mobileSearchOpening) return;

    // Only Search is a non-blocking header extension on desktop pointer devices.
    if (this.id === 'search-modal' && matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').matches) {
      this.#showDesktopSearch();
      return;
    }

    if (this.id === 'search-modal' && isMobileHeaderPanel()) {
      this.#mobileSearchOpening = true;
      const drawer = document.querySelector('#header-component header-drawer');
      if (drawer?.isOpen) await drawer.close();
      if (!this.isConnected) { this.#mobileSearchOpening = false; return; }
    }
    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockScroll(dialog);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
      this.#mobileSearchOpening = false;
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    if (this.#desktopSearchOpen) {
      await this.#closeDesktopSearch(true);
      return;
    }

    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    const mobileSearchLock = this.id === 'search-modal' && document.documentElement.hasAttribute('data-mobile-header-lock');
    unlockScroll(dialog);
    // The shared mobile lock restores the frozen page; other dialogs keep native behavior.
    if (!mobileSearchLock) scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /** Search-only modeless mode; other dialogs retain their modal scroll ownership. */
  #showDesktopSearch() {
    const { dialog } = this.refs;
    const focused = document.activeElement;
    this.#desktopSearchTrigger = focused instanceof HTMLElement && focused.getAttribute('on:click') === '#search-modal/showDialog' ? focused :
      Array.from(document.querySelectorAll('#header-component button')).find((button) => button.getAttribute('on:click') === '#search-modal/showDialog' && button.offsetWidth > 0) ?? null;
    this.#desktopSearchOpen = true;
    this.#desktopSearchClosing = false;
    this.#desktopSearchController?.abort();
    this.#desktopSearchController = new AbortController();
    const { signal } = this.#desktopSearchController;
    const headerBottom = document.querySelector('#header-component')?.getBoundingClientRect().bottom ?? 73;
    const panelTop = headerBottom - this.getBoundingClientRect().top;
    dialog.style.setProperty('--tb-search-desktop-top', `${panelTop}px`);
    dialog.setAttribute('data-search-desktop', '');
    dialog.setAttribute('aria-modal', 'false');
    dialog.show();
    const input = dialog.querySelector('input[autofocus]');
    if (input instanceof HTMLInputElement) input.focus({ preventScroll: true });
    this.dispatchEvent(new DialogOpenEvent());

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    document.addEventListener('click', (event) => {
      // The explicit opening click is still bubbling when modeless Search opens.
      const trigger = event.target instanceof Element ? event.target.closest('button') : null;
      if (trigger?.getAttribute('on:click') === '#search-modal/showDialog') return;
      if (event.target instanceof Node && !this.contains(event.target)) this.#closeDesktopSearch(false);
    }, { signal });
    document.addEventListener('wheel', (event) => {
      if (!event.deltaX && !event.deltaY) return;
      const results = event.target instanceof Element ? event.target.closest('.predictive-search-form__content') : null;
      if (results && dialog.contains(results) && results.scrollHeight > results.clientHeight + 1) return;
      // No preventDefault or synthetic scrolling: the same wheel keeps its native scroll chain.
      this.#closeDesktopSearch(false, true);
    }, { passive: true, capture: true, signal });
    const openedScrollTop = getScrollTop();
    getScrollEventTarget().addEventListener('scroll', () => {
      if (getScrollTop() !== openedScrollTop) this.#closeDesktopSearch(false, true);
    }, { passive: true, signal });
    matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').addEventListener('change', () => this.#closeDesktopSearch(false, true), { signal });
  }

  /** @param {boolean} restoreFocus @param {boolean} [immediate=false] */
  async #closeDesktopSearch(restoreFocus, immediate = false) {
    const { dialog } = this.refs;
    if (!this.#desktopSearchOpen || !dialog.open || this.#desktopSearchClosing) return;
    this.#desktopSearchClosing = true;
    this.#desktopSearchController?.abort();
    this.#desktopSearchController = null;
    // Prevent native dialog focus restoration from pulling a scrolling page back to the header.
    if (!restoreFocus && dialog.contains(document.activeElement) && document.activeElement instanceof HTMLElement) document.activeElement.blur();
    if (!immediate) {
      dialog.style.animation = 'none';
      void dialog.offsetWidth;
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';
      await onAnimationEnd(dialog, undefined, { subtree: false });
    }
    dialog.close();
    dialog.classList.remove('dialog-closing');
    dialog.removeAttribute('data-search-desktop');
    dialog.style.removeProperty('--tb-search-desktop-top');
    dialog.removeAttribute('aria-modal');
    this.#desktopSearchOpen = false;
    this.#desktopSearchClosing = false;
    if (restoreFocus && this.#desktopSearchTrigger?.isConnected) this.#desktopSearchTrigger.focus({ preventScroll: true });
    this.#desktopSearchTrigger = null;
    this.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
